'use client';

import { c as _c } from "react/compiler-runtime";
import { useControlled } from '@base-ui/utils/useControlled';
import { useHasHydrated } from '@repo/ui/hooks';
import { cn } from '@repo/ui/lib/utils';
import { Monitor, Moon, Sun } from 'lucide-react';
import { motion } from 'motion/react';
type Theme = 'dark' | 'light' | 'system';
const themes = [{
  icon: Monitor,
  key: 'system',
  label: 'System theme'
}, {
  icon: Sun,
  key: 'light',
  label: 'Light theme'
}, {
  icon: Moon,
  key: 'dark',
  label: 'Dark theme'
}] as const;
export type ThemeSwitcherProps = {
  className?: string;
  defaultValue?: Theme;
  onChange?: (theme: Theme) => void;
  value?: Theme;
};
export const ThemeSwitcher = t0 => {
  const $ = _c(15);
  const {
    className,
    defaultValue: t1,
    onChange,
    value
  } = t0;
  const defaultValue = t1 === undefined ? "system" : t1;
  let t2;
  if ($[0] !== defaultValue || $[1] !== value) {
    t2 = {
      controlled: value,
      default: defaultValue,
      name: "ThemeSwitcher",
      state: "theme"
    };
    $[0] = defaultValue;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [theme, setTheme] = useControlled(t2);
  const hasHydrated = useHasHydrated();
  let t3;
  if ($[3] !== onChange || $[4] !== setTheme || $[5] !== theme) {
    t3 = themeKey => {
      if (themeKey === theme) {
        return;
      }
      onChange?.(themeKey);
      setTheme(themeKey);
    };
    $[3] = onChange;
    $[4] = setTheme;
    $[5] = theme;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleThemeClick = t3;
  if (!hasHydrated) {
    return null;
  }
  let t4;
  if ($[7] !== className) {
    t4 = cn("relative isolate flex h-8 rounded-full bg-background p-1 ring-1 ring-border", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== handleThemeClick || $[10] !== theme) {
    t5 = themes.map(t6 => {
      const {
        icon: Icon,
        key,
        label
      } = t6;
      const isActive = theme === key;
      return <button aria-label={label} className="relative h-6 w-6 rounded-full" key={key} onClick={() => handleThemeClick(key)} type="button">{isActive && <motion.div className="absolute inset-0 rounded-full bg-secondary" layoutId="activeTheme" transition={{
          duration: 0.5,
          type: "spring"
        }} />}<Icon className={cn("relative z-10 m-auto h-4 w-4", isActive ? "text-foreground" : "text-muted-foreground")} /></button>;
    });
    $[9] = handleThemeClick;
    $[10] = theme;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className={t4} data-sentry-component="ThemeSwitcher" data-sentry-source-file="index.tsx">{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};